import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Layout from '../../components/Layout/Layout';
import LeadsTabs from './LeadsTabs';
import {
  Card, Button, Badge, Modal, Tooltip, EmptyState, Spinner, Select,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '../../components/UI';
import { territoriesAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlineLocationMarker, HiOutlinePlus, HiOutlinePencil, HiOutlineTrash,
  HiOutlineEye, HiOutlineUserGroup,
} from 'react-icons/hi';
import TerritoryModal from './TerritoryModal';

const STATUS_BADGES = {
  new: 'new', open: 'info', in_progress: 'primary', follow_up: 'follow_up',
  cold: 'cold', warm: 'warm', hot: 'hot', converted: 'converted',
  closed: 'closed', lost: 'danger', not_connected: 'not_connected',
};

const Territories = () => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [selectedTerritory, setSelectedTerritory] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [territoryToDelete, setTerritoryToDelete] = useState(null);
  const [viewingTerritory, setViewingTerritory] = useState(null);

  const { data: territoriesData, isLoading } = useQuery({
    queryKey: ['territories'],
    queryFn: async () => {
      const response = await territoriesAPI.getAll();
      return response.data?.data || [];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => territoriesAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['territories']);
      toast.success('Territory deleted successfully');
      setShowDeleteModal(false);
      setTerritoryToDelete(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete territory');
    },
  });

  const territories = territoriesData || [];

  const describeGeo = (t) => {
    const parts = [];
    if ((t.states || []).length > 0) parts.push(`States: ${t.states.join(', ')}`);
    if ((t.city_ids || []).length > 0) parts.push(`Cities: ${t.city_ids.join(', ')}`);
    if ((t.pincodes || []).length > 0) parts.push(`Pincodes: ${t.pincodes.join(', ')}`);
    return parts;
  };

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-4 sm:space-y-6">
        {/* Leads section tabs */}
        <LeadsTabs />
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Territories</h1>
            <p className="text-gray-500 text-xs sm:text-sm mt-0.5 sm:mt-1">
              Assign and manage leads by geographic region — area-specific agents handle their region's leads
            </p>
          </div>
          <Button
            variant="primary"
            size="sm"
            icon={<HiOutlinePlus className="w-4 h-4" />}
            onClick={() => { setSelectedTerritory(null); setShowModal(true); }}
          >
            <span className="hidden sm:inline">New Territory</span>
            <span className="sm:hidden">New</span>
          </Button>
        </div>

        {/* Territories grid */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : territories.length === 0 ? (
          <Card>
            <EmptyState
              icon={<HiOutlineLocationMarker className="w-8 h-8 text-gray-400" />}
              title="No territories yet"
              description="Create territories by state, city or pincode and assign area-specific agents — new leads are then auto-routed to the right region"
            />
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
            {territories.map((t) => (
              <Card key={t._id} className="flex flex-col justify-between hover:shadow-lg transition-all duration-200">
                <div>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span
                        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${t.color || '#10B981'}1A` }}
                      >
                        <HiOutlineLocationMarker className="w-5 h-5" style={{ color: t.color || '#10B981' }} />
                      </span>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{t.name}</p>
                        <p className="text-xs text-gray-400">
                          {t.lead_count ?? 0} lead{(t.lead_count ?? 0) === 1 ? '' : 's'} in region
                        </p>
                      </div>
                    </div>
                  </div>

                  {t.description && (
                    <p className="text-xs text-gray-500 line-clamp-2 mb-3">{t.description}</p>
                  )}

                  <div className="flex flex-wrap gap-1 mb-3">
                    {describeGeo(t).map((part, i) => (
                      <Badge key={i} variant="default" size="sm">{part}</Badge>
                    ))}
                  </div>

                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                    <HiOutlineUserGroup className="w-4 h-4" />
                    {(t.assigned_user_ids || []).length === 0 ? (
                      <span className="text-warning-600 font-medium">No agents assigned</span>
                    ) : (
                      <span>
                        {(t.assigned_user_ids || []).slice(0, 3).map((u) => (
                          <span key={u._id} className="mr-1">
                            {u.firstName} {u.lastName}
                          </span>
                        ))}
                        {(t.assigned_user_ids || []).length > 3 && (
                          <span>+{(t.assigned_user_ids || []).length - 3} more</span>
                        )}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-end gap-1 pt-2 border-t border-gray-100">
                  <Tooltip text="View & distribute leads">
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<HiOutlineEye className="w-4 h-4 text-gray-500" />}
                      onClick={() => setViewingTerritory(t)}
                      title="View leads"
                    />
                  </Tooltip>
                  <Tooltip text="Edit territory">
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<HiOutlinePencil className="w-4 h-4 text-gray-500" />}
                      onClick={() => { setSelectedTerritory(t); setShowModal(true); }}
                      title="Edit"
                    />
                  </Tooltip>
                  <Tooltip text="Delete territory">
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={<HiOutlineTrash className="w-4 h-4 text-red-500" />}
                      onClick={() => { setTerritoryToDelete(t); setShowDeleteModal(true); }}
                      title="Delete"
                    />
                  </Tooltip>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Create / Edit Modal */}
        <TerritoryModal
          isOpen={showModal}
          onClose={() => { setShowModal(false); setSelectedTerritory(null); }}
          territory={selectedTerritory}
        />

        {/* Leads modal */}
        <TerritoryLeadsModal territory={viewingTerritory} onClose={() => setViewingTerritory(null)} />

        {/* Delete confirmation */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Delete Territory"
          size="sm"
        >
          <div className="space-y-3 sm:space-y-4">
            <p className="text-gray-600 text-sm">
              Are you sure you want to delete <span className="font-semibold">{territoryToDelete?.name}</span>? Leads are not affected — only the territory definition is removed.
            </p>
            <div className="flex gap-2 sm:gap-3 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => territoryToDelete && deleteMutation.mutate(territoryToDelete._id)}
                loading={deleteMutation.isPending}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </Layout>
  );
};

// ========================================
// Leads inside a territory — with round-robin distribution
// ========================================
const TerritoryLeadsModal = ({ territory, onClose }) => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [assignmentFilter, setAssignmentFilter] = useState('unassigned');
  const pageSize = 20;

  const isOpen = Boolean(territory?._id);

  const { data: leadsData, isLoading } = useQuery({
    queryKey: ['territoryLeads', territory?._id, page, assignmentFilter],
    queryFn: async () => {
      const response = await territoriesAPI.getLeads(territory._id, {
        page,
        limit: pageSize,
        assignment: assignmentFilter,
      });
      return response.data || {};
    },
    enabled: isOpen,
  });

  const agentCount = (territory?.assigned_user_ids || []).filter((u) => u && u.isActive !== false).length;

  const assignMutation = useMutation({
    mutationFn: () => territoriesAPI.assignLeads(territory._id, false),
    onSuccess: (response) => {
      const d = response.data?.data || {};
      toast.success(response.data?.message || `Distributed ${d.assigned} lead(s)`);
      queryClient.invalidateQueries(['territories']);
      queryClient.invalidateQueries(['territoryLeads']);
      setPage(1);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to distribute territory leads');
    },
  });

  const leads = leadsData?.data || [];
  const total = leadsData?.pagination?.total || 0;
  const totalPages = leadsData?.pagination?.totalPages || 1;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={territory ? `${territory.name} — Leads` : 'Leads'} size="xl">
      <div className="space-y-4">
        {/* Distribution bar */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 bg-secondary-50 rounded-xl border border-secondary-200">
          <div className="flex-1 min-w-0">
            {agentCount === 0 ? (
              <>
                <p className="text-sm text-warning-700 bg-warning-50 border border-warning-200 rounded-lg px-3 py-2">
                  No agents assigned to this territory. Edit the territory and select telecallers in CRM → Leads → Territories first.
                </p>
                <p className="text-xs text-secondary-400 mt-1">
                  New leads matching this territory are also auto-assigned automatically.
                </p>
              </>
            ) : (
              <p className="text-sm font-semibold text-secondary-700">
                Distribute this region's unassigned leads round-robin across {agentCount} territory agent{agentCount === 1 ? '' : 's'}
              </p>
            )}
          </div>
          <Button
            variant="primary"
            size="sm"
            icon={<HiOutlineUserGroup className="w-4 h-4" />}
            onClick={() => assignMutation.mutate()}
            loading={assignMutation.isPending}
            disabled={agentCount === 0}
            className="flex-shrink-0"
          >
            Distribute now
          </Button>
        </div>

        {/* Assignment filter */}
        <div className="flex items-center gap-3">
          <div className="w-56">
            <Select
              value={assignmentFilter}
              onChange={(e) => { setAssignmentFilter(e.target.value); setPage(1); }}
              options={[
                { value: 'unassigned', label: 'Unassigned leads' },
                { value: 'assigned', label: 'Assigned leads' },
                { value: 'all', label: 'All leads in territory' },
              ]}
            />
          </div>
          <span className="text-xs text-gray-500">{total} lead(s)</span>
        </div>

        {/* Leads table */}
        {isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner size="lg" />
          </div>
        ) : leads.length === 0 ? (
          <EmptyState
            icon={<HiOutlineLocationMarker className="w-8 h-8 text-gray-400" />}
            title="No leads here"
            description={
              assignmentFilter === 'unassigned'
                ? 'Every lead in this territory already has an agent — great!'
                : "No leads currently match this territory's geography"
            }
          />
        ) : (
          <div className="overflow-x-auto border border-secondary-200 rounded-xl">
            <Table>
              <TableHead>
                <TableRow hoverable={false}>
                  <TableCell header>Name</TableCell>
                  <TableCell header>Phone</TableCell>
                  <TableCell header>City</TableCell>
                  <TableCell header>Status</TableCell>
                  <TableCell header>Assigned</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow key={lead._id}>
                    <TableCell>
                      <span className="font-medium text-gray-900">{lead.name}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-600">{lead.phone}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-600 text-sm">
                        {lead.address?.city || lead.address?.state || '—'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_BADGES[lead.status] || 'default'} size="sm">
                        {lead.status?.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {lead.assignedTo ? (
                        <span className="text-gray-700 text-sm">
                          {lead.assignedTo.firstName} {lead.assignedTo.lastName}
                        </span>
                      ) : (
                        <Badge variant="warning" size="sm">Unassigned</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Pagination */}
        {isOpen && total > 0 && (
          <div className="flex items-center justify-between pt-1">
            <span className="text-xs sm:text-sm text-gray-500">
              Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default Territories;